/**
 * Suppliers — who goods are bought from.
 *
 * Replaces the empty placeholder that sat on this route. Deactivate rather
 * than delete: a posted intake names its supplier forever, and although the
 * document carries a snapshot of the name, the id still has to resolve.
 *
 * GRN additions (docs/feature-stock-grn-variants.md §5.3): each row has New
 * intake (opens the editor with the supplier chosen and its last delivery
 * offered) and Intakes (the list filtered to it). Last intake, Intakes and
 * Received 30d come from the server, which counts every posted intake rather
 * than the page of intakes this screen happens to have loaded.
 *
 * See docs/feature-stock-intake-and-suppliers.md §7.3.
 */
import React, {useEffect, useMemo, useState} from 'react';
import {connect} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {Actions} from '../../../internals/app/Actions';
import {
  PAYMENT_TERMS,
  paymentTermLabel,
  type Supplier,
} from '../../../internals/repository/SupplierRepository';
import type {StockIntake} from '../../../internals/repository/StockIntakeRepository';
import {supplierFigures} from '../supplierFigures';

const EMPTY: Supplier = {
  supplierId: '',
  name: '',
  contactName: '',
  phone: '',
  email: '',
  address: '',
  paymentTerms: 'cash',
  notes: '',
};

const inputCls = 'nl-input';
const selectCls = 'nl-select';
const labelCls = 'nl-label';

/** Money for the "received in 30 days" column. */
const fmt = (n: number, currency?: string) =>
  `${currency || ''} ${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`.trim();

const SupplierView = (props: any) => {
  const {
    suppliers,
    intakes,
    selectedShop,
    stockError,
    fetchSuppliers,
    fetchIntakes,
    createSupplier,
    updateSupplier,
    deactivateSupplier,
  } = props;

  const shopId = selectedShop?.shopId;
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [form, setForm] = useState<Supplier>(EMPTY);
  const [formError, setFormError] = useState('');
  const [confirmOff, setConfirmOff] = useState<Supplier | null>(null);

  useEffect(() => {
    if (!shopId) return;
    fetchSuppliers(shopId, undefined, true);
    // Intakes feed the "last delivery" and "this month" columns. Read-only
    // here; the list screen owns them.
    fetchIntakes(shopId, {limit: 200});
  }, [shopId, fetchSuppliers, fetchIntakes]);

  /**
   * Per-supplier delivery figures, from the intakes already in the store.
   *
   * Void documents are excluded: a reversed delivery is not money spent.
   * Drafts too — nothing has arrived yet.
   */
  const stats = useMemo(() => {
    const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    const acc: Record<string, {last?: string; total30d: number; count: number}> = {};

    for (const intake of (intakes ?? []) as StockIntake[]) {
      if (intake.status !== 'posted' || !intake.supplierId) continue;
      const row = acc[intake.supplierId] ?? {total30d: 0, count: 0};
      row.count += 1;
      if (!row.last || intake.receivedAt > row.last) row.last = intake.receivedAt;
      if (intake.receivedAt >= since) row.total30d += Number(intake.total) || 0;
      acc[intake.supplierId] = row;
    }
    return acc;
  }, [intakes]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return ((suppliers ?? []) as Supplier[]).filter((s) => {
      if (!showInactive && s.isActive === false) return false;
      if (!q) return true;
      return [s.name, s.contactName, s.phone, s.email]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [suppliers, search, showInactive]);

  const openNew = () => {
    setForm(EMPTY);
    setFormError('');
    setEditing(EMPTY);
  };

  const openEdit = (s: Supplier) => {
    setForm({...EMPTY, ...s});
    setFormError('');
    setEditing(s);
  };

  const save = () => {
    if (!form.name.trim()) {
      setFormError('A name is required.');
      return;
    }
    const payload = {
      name: form.name.trim(),
      contactName: form.contactName || null,
      phone: form.phone || null,
      email: form.email || null,
      address: form.address || null,
      paymentTerms: form.paymentTerms || null,
      notes: form.notes || null,
    };
    if (editing?.supplierId) {
      updateSupplier(shopId, {supplierId: editing.supplierId, ...payload});
    } else {
      createSupplier(shopId, payload);
    }
    setEditing(null);
  };

  const currency = selectedShop?.currency;

  return (
    <div className="h-screen flex flex-col w-full p-6">
      <div className="flex-shrink-0 pb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-text1">Suppliers</h1>
          <p className="text-sm text-text2 mt-1">
            Who goods are bought from. Deactivating keeps a supplier on past
            deliveries but hides them from new ones.
          </p>
        </div>
        <button
          type="button"
          onClick={openNew}
          className="nl-btn nl-btn--primary"
        >
          Add supplier
        </button>
      </div>

      {stockError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-800">
          {stockError}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 pb-4">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, contact, phone or email…"
          className="nl-input flex-1 w-auto min-w-[220px]"
        />
        <label className="flex h-10 cursor-pointer select-none items-center gap-2 text-sm text-text2">
          <input
            type="checkbox"
            className="nl-check"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show deactivated
        </label>
      </div>

      <div className="flex-1 min-h-0 overflow-auto rounded-xl border border-borderc bg-surface">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-surface2 text-text2">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Name</th>
              <th className="px-4 py-3 text-left font-medium">Contact</th>
              <th className="px-4 py-3 text-left font-medium">Phone</th>
              <th className="px-4 py-3 text-left font-medium">Terms</th>
              <th className="px-4 py-3 text-left font-medium">Last intake</th>
              <th className="px-4 py-3 text-right font-medium">Intakes</th>
              <th className="px-4 py-3 text-right font-medium">Received 30d</th>
              <th className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-text2">
                  {search ? 'No supplier matches that search.' : 'No suppliers yet.'}
                </td>
              </tr>
            )}
            {visible.map((s) => {
              const st = supplierFigures(s, stats[s.supplierId]);
              const inactive = s.isActive === false;
              return (
                <tr
                  key={s.supplierId}
                  className={`border-t border-borderc ${inactive ? 'opacity-55' : ''}`}
                >
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => navigate(`/stock?supplierId=${s.supplierId}`)}
                      className="font-medium text-text1 hover:text-brand"
                      title="Show this supplier's deliveries"
                    >
                      {s.name}
                    </button>
                    {inactive && (
                      <span className="ml-2 rounded-full bg-surface2 px-2 py-0.5 text-[11px] text-text2">
                        Deactivated
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-text2">{s.contactName || '—'}</td>
                  <td className="px-4 py-3 text-text2">{s.phone || '—'}</td>
                  <td className="px-4 py-3 text-text2">{paymentTermLabel(s.paymentTerms)}</td>
                  <td className="px-4 py-3 text-text2">
                    {st.last ? new Date(st.last).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    <button
                      type="button"
                      onClick={() => navigate(`/stock?supplierId=${encodeURIComponent(s.supplierId)}`)}
                      className="text-text2 hover:text-brand"
                      title="Show this supplier's intakes"
                    >
                      {st.count}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-text1">
                    {fmt(st.total30d, currency)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      {!inactive && (
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/stock/intake/new?supplierId=${encodeURIComponent(s.supplierId)}`)
                          }
                          className="nl-btn nl-btn--primary nl-btn--sm"
                        >
                          New intake
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => navigate(`/stock?supplierId=${encodeURIComponent(s.supplierId)}`)}
                        className="nl-btn nl-btn--secondary nl-btn--sm"
                      >
                        Intakes
                      </button>
                      <button
                        type="button"
                        onClick={() => openEdit(s)}
                        className="nl-btn nl-btn--secondary nl-btn--sm"
                      >
                        Edit
                      </button>
                      {!inactive ? (
                        <button
                          type="button"
                          onClick={() => setConfirmOff(s)}
                          className="nl-btn nl-btn--secondary nl-btn--sm"
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            updateSupplier(shopId, {supplierId: s.supplierId, isActive: true})
                          }
                          className="nl-btn nl-btn--secondary nl-btn--sm"
                        >
                          Reactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="nl-backdrop">
          <div className="nl-modal max-w-lg">
            <div className="border-b border-borderc px-5 py-4">
              <h2 className="text-base font-semibold text-text1">
                {editing.supplierId ? 'Edit supplier' : 'Add supplier'}
              </h2>
            </div>
            <div className="grid grid-cols-2 gap-3 p-5">
              <div className="col-span-2">
                <span className={labelCls}>Name *</span>
                <input
                  className={inputCls}
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  placeholder="Colombo Wholesale"
                />
              </div>
              <div>
                <span className={labelCls}>Contact person</span>
                <input
                  className={inputCls}
                  value={form.contactName ?? ''}
                  onChange={(e) => setForm({...form, contactName: e.target.value})}
                />
              </div>
              <div>
                <span className={labelCls}>Phone</span>
                <input
                  className={inputCls}
                  value={form.phone ?? ''}
                  onChange={(e) => setForm({...form, phone: e.target.value})}
                />
              </div>
              <div>
                <span className={labelCls}>Email</span>
                <input
                  className={inputCls}
                  value={form.email ?? ''}
                  onChange={(e) => setForm({...form, email: e.target.value})}
                />
              </div>
              <div>
                <span className={labelCls}>Payment terms</span>
                <select
                  className={selectCls}
                  value={form.paymentTerms ?? ''}
                  onChange={(e) => setForm({...form, paymentTerms: e.target.value})}
                >
                  {PAYMENT_TERMS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <span className={labelCls}>Address</span>
                <input
                  className={inputCls}
                  value={form.address ?? ''}
                  onChange={(e) => setForm({...form, address: e.target.value})}
                />
              </div>
              <div className="col-span-2">
                <span className={labelCls}>Notes</span>
                <textarea
                  className={`${inputCls} h-20 resize-none py-2`}
                  value={form.notes ?? ''}
                  onChange={(e) => setForm({...form, notes: e.target.value})}
                />
              </div>
              {formError && (
                <p className="col-span-2 text-xs text-red-600">{formError}</p>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-borderc px-5 py-4">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="nl-btn nl-btn--secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                className="nl-btn nl-btn--primary"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmOff && (
        <div className="nl-backdrop">
          <div className="nl-modal max-w-sm p-5">
            <h2 className="text-base font-semibold text-text1">
              Deactivate {confirmOff.name}?
            </h2>
            <p className="mt-2 text-sm text-text2">
              Past deliveries keep this supplier. It stops appearing when you
              create a new intake, and you can reactivate it at any time.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmOff(null)}
                className="nl-btn nl-btn--secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  deactivateSupplier(shopId, confirmOff.supplierId);
                  setConfirmOff(null);
                }}
                className="nl-btn nl-btn--primary"
              >
                Deactivate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default connect(
  // `state.stock` is an Immutable Map, so these are .get() calls — reading it
  // as a plain object is what left the Stock Levels table permanently empty.
  (state: any) => ({
    suppliers: state.stock.get('suppliers'),
    intakes: state.stock.get('intakes'),
    stockError: state.stock.get('stockError'),
    selectedShop: state.dashboard.get('selectedShop'),
  }),
  {
    fetchSuppliers: Actions.stock.fetchSuppliers,
    fetchIntakes: Actions.stock.fetchIntakes,
    createSupplier: Actions.stock.createSupplier,
    updateSupplier: Actions.stock.updateSupplier,
    deactivateSupplier: Actions.stock.deactivateSupplier,
  },
)(SupplierView);
